// Checkout add-ons the shopper can tick (gift box, card …). `addons` = the store's ACTIVE add-ons; `selectedIds` = ticked ids.
// Each row shows the seller's picture (tap it to see it bigger in a pop-up), the name and the price. Used in the cart and in Buy Now.
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

// Full-size picture pop-up. Rendered in a portal so no parent (e.g. the Buy Now address modal) can clip it; React still bubbles its
// events to the parent tree, so every handler stops propagation — closing the picture must never close the modal behind it.
function PicPopup({ src, name, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") { e.stopPropagation(); onClose(); } };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [onClose]);
  const stop = (fn) => (e) => { e.stopPropagation(); fn && fn(); };
  return createPortal(
    <div data-addon-popup role="dialog" aria-modal="true" aria-label={name} onClick={stop(onClose)}
      style={{ position: "fixed", inset: 0, zIndex: 100000, background: "rgba(0,0,0,.78)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={stop()} style={{ position: "relative", maxWidth: "min(92vw, 560px)", maxHeight: "88vh", background: "var(--white)", borderRadius: 4, padding: 10, boxShadow: "0 20px 60px rgba(0,0,0,.4)" }}>
        <button type="button" aria-label="Close picture" onClick={stop(onClose)}
          style={{ position: "absolute", top: -12, right: -12, width: 32, height: 32, borderRadius: "50%", border: "none", background: "var(--white)", boxShadow: "0 2px 10px rgba(0,0,0,.3)", fontSize: 18, lineHeight: 1, cursor: "pointer", fontWeight: 700 }}>✕</button>
        <img src={src} alt={name} style={{ display: "block", maxWidth: "100%", maxHeight: "calc(88vh - 60px)", objectFit: "contain", borderRadius: 4, margin: "0 auto" }} />
        <div style={{ textAlign: "center", fontSize: 14, fontWeight: 700, padding: "8px 4px 2px", wordBreak: "break-word" }}>{name}</div>
      </div>
    </div>,
    document.body
  );
}

export function AddonPicker({ addons, selectedIds = [], onToggle, title = "Add-ons", note = "Optional extras from this store" }) {
  const [big, setBig] = useState(null);   // { src, name } of the picture being viewed
  if (!Array.isArray(addons) || addons.length === 0) return null;
  const on = new Set(selectedIds.map(String));
  return (
    <div data-addon-picker style={{ marginTop: 14, padding: 14, border: "1.5px solid var(--border-mid)", borderRadius: 4, background: "var(--surface)" }}>
      <div style={{ fontWeight: 700, fontSize: 14 }}>{title}</div>
      <div style={{ fontSize: 11, color: "var(--ink-soft)", margin: "2px 0 10px" }}>{note}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {addons.map(a => {
          const sel = on.has(String(a.id));
          return (
            <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: 10, borderRadius: 4, background: "var(--white, #fff)", border: `1.5px solid ${sel ? "var(--teal)" : "var(--border)"}` }}>
              {a.image && (
                <button type="button" aria-label={`See ${a.name} bigger`} onClick={(e) => { e.stopPropagation(); setBig({ src: a.image, name: a.name }); }}
                  style={{ flexShrink: 0, lineHeight: 0, padding: 0, border: "none", background: "none", cursor: "zoom-in" }}>
                  <img src={a.image} alt={a.name} style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 4, border: "1px solid var(--border)" }} />
                </button>
              )}
              <label style={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: 10, cursor: "pointer", margin: 0 }}>
                <input type="checkbox" checked={sel} onChange={() => onToggle && onToggle(a.id)} aria-label={`Add ${a.name} for ₹${a.price}`} style={{ width: 20, height: 20, accentColor: "var(--teal)", flexShrink: 0 }} />
                <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, wordBreak: "break-word" }}>{a.name}</span>
                <span style={{ fontSize: 14, fontWeight: 700, color: "var(--teal)", flexShrink: 0 }}>{Number(a.price) > 0 ? `+₹${Number(a.price).toLocaleString("en-IN")}` : "Free"}</span>
              </label>
            </div>
          );
        })}
      </div>
      {big && <PicPopup src={big.src} name={big.name} onClose={() => setBig(null)} />}
    </div>
  );
}
